import { FunctionComponent } from 'react';
import { Loader } from '~components/Loader';
import Currency from '~components/Currency';
import { Price } from '~ui/index';

import { GetName, SteamItemImage } from '~components/item';

import { Event as EventTypes } from '~types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { useGetProfileEventProgress } from '~components/hooks';

declare module '~types' {
  interface EventUser {
    nextPrizeSpent: number;
  }
}

interface Props {
  getEvent: EventTypes;
}

export const Progress: FunctionComponent<Props> = ({ getEvent }) => {
  const { t } = useTranslation();
  const { data, loading, error } = useGetProfileEventProgress(getEvent?.id);

  const getProfile = data?.getProfile;

  if (!getProfile?.isPlayEvent) {
    return null;
  }

  if (loading) {
    return <Loader />;
  } else if (error) {
    return <>Progress | Error! {error.message}</>;
  }

  const { nextRank, getItem, getNextItem, ranking } =
    getProfile?.getEventProgress;

  return (
    <div className="breezy-progress">
      <div className="breezy-progress-prize">
        <div className="in-case-wrap">
          {ranking > getEvent?.totalRank ? (
            <div className="in-case-item empty">
              <div className="in-case-breezy-title">
                <span>{t('breezy-progress in-case-breezy-title')}</span>
              </div>
              <div className="in-case-item-wrap">
                <div className="img">
                  <div className="winner-num">
                    <span>#{ranking}</span>
                  </div>
                </div>
                <div className="in-case-info">
                  <div className="in-case-item-name">
                    {t(
                      'breezy-progress-prize in-case-item-wrap in-case-info in-case-item-name',
                      { totalRank: getEvent?.totalRank },
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="in-case-item active status-1" data-rar={getItem?.color}>
              <div className="in-case-breezy-title">
                <span>{t('breezy-progress in-case-breezy-title')}</span>
              </div>
              <div className="in-case-item-wrap">
                <div className="img">
                  <div className="in-case-item-bg" />
                  <div className="winner-num">
                    <span>#{ranking}</span>
                  </div>
                  <SteamItemImage url={getItem?.getImage} size="350x350" />
                </div>
                <div className="in-case-info">
                  <div className="in-case-item-name">
                    <GetName getItem={getItem} type={0} />
                  </div>
                  <div className="in-case-item-desc">
                    <GetName getItem={getItem} type={1} />
                  </div>
                </div>
              </div>
            </div>
          )}
          {ranking === 1 ? (
            <div className="in-case-item empty">
              <div className="in-case-breezy-title">
                <span>{t('breezy-progress in-case-breezy-title next')}</span>
              </div>
              <div className="in-case-item-wrap">
                <div className="img"></div>
                <div className="in-case-info">
                  <div className="in-case-item-name">
                    {t('breezy-progress in-case-item-wrap in-case-item-name')}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div
              className="in-case-item active status-1"
              data-rar={getNextItem?.color}
            >
              <div className="in-case-breezy-title">
                <span>{t('breezy-progress in-case-breezy-title next')}</span>
              </div>
              <div className="in-case-item-wrap">
                <div className="img">
                  <div className="in-case-item-bg" />
                  <div className="winner-num">
                    <span>#{nextRank}</span>
                  </div>
                  <SteamItemImage url={getNextItem?.getImage} size="350x350" />
                </div>
                <div className="in-case-info">
                  <div className="in-case-item-name">
                    <GetName getItem={getNextItem} type={0} />
                  </div>
                  <div className="in-case-item-desc">
                    <GetName getItem={getNextItem} type={1} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="breezy-profile">
          <div className="breezy-profile-stats">
            {!!getProfile?.getEventProgress?.countOpen && (
              <>
                <div className="breezy-profile-stats-item">
                  <div className="breezy-profile-stats-num">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                    >  
                      <defs>
                        <linearGradient
                          id="linear-gradient1"
                          x1="0.5" 
                          y1="1" 
                          x2="0.5"
                          gradientUnits="objectBoundingBox"
                        >
                          <stop offset="0" stopColor="#8214a8"></stop>
                          <stop offset="1" stopColor="#ff6ce3"></stop>
                        </linearGradient>
                      </defs>
                      <g transform="translate(-4859 -550)">
                        <rect
                          width="32"
                          height="32"
                          transform="translate(4859 550)"
                          fill="#fff"
                          opacity="0"
                        ></rect>
                        <path
                          d="M2837,2262v-8h6.026v8Zm-14,0v-8h4v2h5v-2h4v8Zm8-7h-3v-3h3v3Zm-2-1h1v-1h-1Zm8-5c.009-5.125,6-5.082,6,0,0,2.8,0,3.732,0,4h-6s0,.1,0,.1S2836.994,2252.562,2837,2249Zm6,4h0s0,.1,0,.1S2843,2253.073,2843,2253Zm-11,0v-2h-5v2h-4s.011-1.027,0-4a3.88,3.88,0,0,1,4-4c1.554.008,11,0,11,0a4.289,4.289,0,0,0-2,4c.023,3,0,4,0,4Z"
                          transform="translate(2042 -1687.001)"
                          fill="url(#linear-gradient1)"
                        ></path>
                      </g>
                    </svg>
                    <span>{getProfile?.getEventProgress?.countOpen}</span>
                  </div>
                  <div className="breezy-profile-stats-label">
                    {t('case_two')}
                  </div>
                </div>
                <div className="breezy-profile-stats-item">
                  <div className="breezy-profile-stats-num">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                    >
                      <defs>
                        <linearGradient
                          id="linear-gradient2"
                          x1="0.5"
                          y1="1"
                          x2="0.5"
                          gradientUnits="objectBoundingBox"
                        >
                          <stop offset="0" stopColor="#8214a8"></stop>
                          <stop offset="1" stopColor="#ff6ce3"></stop>
                        </linearGradient>
                      </defs>
                      <g transform="translate(-4859 -550)">
                        <rect
                          width="32"
                          height="32"
                          transform="translate(4859 550)"
                          fill="#fff"
                          opacity="0"
                        ></rect>
                        <path
                          d="M2689.75,2268.242c-1.854-.843-2.728-2.509-2.75-5.242a8.484,8.484,0,0,1,2.481-6.242A8.589,8.589,0,0,1,2692,2255c.021.02,2.095,2,4,2s3.979-1.98,4-2a8.512,8.512,0,0,1,2.487,1.758A8.706,8.706,0,0,1,2705,2263a5.821,5.821,0,0,1-1.389,4.337C2701.96,2269,2699.065,2269,2696,2269,2693.643,2269,2691.417,2269,2689.75,2268.242Zm2.25-3.236h2v2h1v-2h2v-1h-2v-1h3a2.521,2.521,0,0,0,0-5c-.833,0-1.618-.005-2.337-.005-.987,0-1.434,0-1.547,0H2694v4h-2v1h2v1h-2Zm6-3c-.558,0-.979,0-1.327,0l-.732,0H2695v-3h.011c.242,0,.751,0,1.441,0,.614,0,1.092,0,1.548,0a1.368,1.368,0,0,1,1.3,1.513,1.351,1.351,0,0,1-1.292,1.488ZM2692,2251c.014-2.806,2-2,2-2a2.486,2.486,0,0,1,2-1,3.836,3.836,0,0,1,2,1s1.994-.976,2,2-2.066,5-4,5S2691.987,2253.806,2692,2251Z"
                          transform="translate(2179 -1693)"
                          fill="url(#linear-gradient2)"
                        ></path>
                      </g>
                    </svg>
                    <span>
                      <Price sum={getProfile?.getEventProgress?.spent} />
                      <span className="cur">
                        <Currency />
                      </span>
                    </span>
                  </div>
                  <div className="breezy-profile-stats-label">
                    {t('breezy-progress breezy-profile-stats-label 1')}
                  </div>
                </div>
              </>
            )}
            {ranking > 1 && (
              <div className="breezy-profile-stats-item">
                <div className="breezy-profile-stats-num">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                  >
                    <defs>
                      <linearGradient
                        id="linear-gradient1"
                        x1="0.5"
                        y1="1"
                        x2="0.5"
                        gradientUnits="objectBoundingBox"
                      >
                        <stop offset="0" stopColor="#8214a8"></stop>
                        <stop offset="1" stopColor="#ff6ce3"></stop>
                      </linearGradient>
                    </defs>
                    <g transform="translate(-4859 -550)">
                      <rect
                        width="32"
                        height="32"
                        transform="translate(4859 550)"
                        fill="#fff"
                        opacity="0"
                      ></rect>
                      <path
                        d="M2837,2262v-8h6.026v8Zm-14,0v-8h4v2h5v-2h4v8Zm8-7h-3v-3h3v3Zm-2-1h1v-1h-1Zm8-5c.009-5.125,6-5.082,6,0,0,2.8,0,3.732,0,4h-6s0,.1,0,.1S2836.994,2252.562,2837,2249Zm6,4h0s0,.1,0,.1S2843,2253.073,2843,2253Zm-11,0v-2h-5v2h-4s.011-1.027,0-4a3.88,3.88,0,0,1,4-4c1.554.008,11,0,11,0a4.289,4.289,0,0,0-2,4c.023,3,0,4,0,4Z"
                        transform="translate(2042 -1687.001)"
                        fill="url(#linear-gradient1)"
                      ></path>
                    </g>
                  </svg>

                  <span>
                    <Price sum={getProfile?.getEventProgress?.nextPrizeSpent} />
                    <span className="cur">
                      <Currency />
                    </span>
                  </span>
                </div>
                <div className="breezy-profile-stats-label">
                  {t(
                    'case-breezy case-breezy-container case-breezy-stats-label',
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
